import { Handshake, Heart, Users, Music, Award } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

function useCountUp(target: number, duration = 1000) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const stepTime = Math.abs(Math.floor(duration / target));
    const timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= target) clearInterval(timer);
    }, stepTime);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return { ref, count };
}

export default function InfoSection() {
  const mapEmbedUrl =
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2891.7053802777405!2d1.480889711828863!3d43.55018295896689!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12aebc27f6d37f7d%3A0xe14b6e878a6c9857!2sSalle%20des%20F%C3%AAtes%20de%20Ramonville%20Saint-Agne!5e0!3m2!1sfr!2sfr!4v1761741619433!5m2!1sfr!2sfr";

  const { ref: refEditions, count: countEditions } = useCountUp(6);
  const { ref: refPartenaires, count: countPartenaires } = useCountUp(16);
  const { ref: refBenevoles, count: countBenevoles } = useCountUp(50);
  const { ref: refConcerts, count: countConcerts } = useCountUp(33);
  const { ref: refSpectateurs, count: countSpectateurs } = useCountUp(1000, 800);

  const generalInfo = [
    "Entrée gratuite",
    "Sans réservation",
    "Accessible PMR",
    "Buvette et restauration sur place",
    "Ambiance conviviale garantie",
  ];

  return (
    <section id="info" className="relative py-20 bg-orange text-bleuFonce overflow-hidden">
      <div className="relative container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 z-10">
        {/* Grille principale */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-start">
          {/* --- Colonne 1 : chiffres + carte --- */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="order-4 md:order-1 md:col-span-3 flex flex-col items-center w-full space-y-8"
          >
            <div className="bg-white shadow-md p-6 w-full text-center">
              <h3 className="text-4xl font-bebas text-bleuClair mb-6">En chiffres</h3>
              <div className="space-y-4">
                {[
                  {
                    icon: <Award className="text-jaune" size={28} />,
                    text: `${countEditions}e édition`,
                    sub: "en 2026",
                    ref: refEditions,
                  },
                  {
                    icon: <Music className="text-jaune" size={28} />,
                    text: `${countConcerts} concerts`,
                    sub: "donnés depuis le début",
                    ref: refConcerts,
                  },
                  {
                    icon: <Handshake className="text-jaune" size={28} />,
                    text: `${countPartenaires} partenaires`,
                    sub: "en 2025",
                    ref: refPartenaires,
                  },
                  {
                    icon: <Heart className="text-jaune" size={28} />,
                    text: `${countBenevoles} bénévoles`,
                    sub: "qui se donnent à fond",
                    ref: refBenevoles,
                  },
                  {
                    icon: <Users className="text-jaune" size={28} />,
                    text: `~${countSpectateurs}`,
                    sub: "spectateurs chaque année",
                    ref: refSpectateurs,
                  },
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center">
                    {item.icon}
                    <p className="text-xl font-bold text-rouge">
                      <span ref={item.ref}>{item.text}</span>
                    </p>
                    <p className="text-sm text-bleuFonce">{item.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {mapEmbedUrl && (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative w-full pb-[56.25%] overflow-hidden shadow-md"
              >
                <iframe
                  title="festival-map"
                  src={mapEmbedUrl}
                  className="absolute top-0 left-0 w-full h-full"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                />
              </motion.div>
            )}
          </motion.div>

          {/* --- Colonne 2 + 3 avec fond micro sur mobile --- */}
          <div className="relative md:col-span-9 flex flex-col md:flex-row md:items-stretch">
            {/* Fond micro visible uniquement sur mobile */}
            <div
              className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-90 md:hidden"
              style={{ backgroundImage: "url('/images/micro.png')" }}
            ></div>

            <div className="grid md:grid-cols-9 gap-8 relative z-10 flex-1">
              {/* --- Colonne 2 : titre + infos générales --- */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
                className="order-2 md:col-span-4 flex flex-col justify-between relative overflow-hidden"
              >
                {/* Fond micro desktop */}
                <div
                  className="hidden md:block absolute inset-0 bg-no-repeat bg-bottom bg-contain opacity-90"
                  style={{ backgroundImage: "url('/images/micro.png')" }}
                ></div>

                {/* Titre */}
                <div>
                  <h2 className="text-6xl lm:text-8xl sm:text-6xl md:text-7xl font-bebas text-rouge leading-tight sm:leading-none mb-4 sm:mb-4 relative z-10">
                    C'est quoi<br/> le FME ?
                  </h2>

                  <div className="h-6 sm:h-12 md:h-20" />
                </div>

                {/* Bloc infos aligné en bas sur tablette & desktop */}
                <motion.div
  initial="hidden"
  whileInView="visible"
  viewport={{ once: true, amount: 0.4 }}
  variants={{
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.25,
      },
    },
  }}
  className="flex flex-col gap-3 w-full relative z-10 mt-6 md:mt-0 md:self-end"
>
  {generalInfo.map((info, i) => (
    <motion.div
      key={i}
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 },
      }}
      transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1], // easeOutExpo-like
      }}
      style={{ willChange: "transform, opacity" }}
      className="bg-bleuClair text-white px-4 py-2 font-semibold text-base md:text-sm shadow-sm 
                 hover:bg-white hover:text-bleuClair transition-colors duration-300"
    >
      {info}
    </motion.div>
  ))}
</motion.div>

              </motion.div>

              {/* --- Colonne 3 : description --- */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
                className="order-3 md:col-span-5 text-bleuFonce text-xs sm:text-sm md:text-[0.95rem] font-medium leading-relaxed sm:leading-relaxed md:leading-loose text-justify space-y-4"
              >
                <p>
                  Un festival de musique d’ensemble organisé chaque année, par l’orchestre
                  d’harmonie La Garonne, qui réunit près de 1000 spectateurs salle Joliot Curie à Ramonville
                  (et un peu le long du canal).
                </p>
                <p>
                  L’entrée est gratuite et durant tout l’après-midi, 6 à 8 ensembles de styles
                  et de formations très différentes se produisent en concert - bigbands,
                  orchestres symphoniques, brassbands, chorales, orchestres d’harmonie -
                  pour le plus grand plaisir des mélomanes comme des curieux.
                </p>
                <p>
                  Une fanfare permet aux spectateurs de profiter de la buvette et d’un
                  foodtruck lors des transitions entre les concerts. Le spectacle se clôture
                  chaque année par un concert de l’harmonie La Garonne.
                </p>
                <p>
                  À chaque édition, son lot de surprises, mélangeant les styles et les ambiances,
                  avec un public conquis et fidèle au rendez-vous !
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
